import gsap from 'gsap';
import Sniffer from 'sniffer';
import SplitText from 'gsap/SplitText';
import MorphSVGPlugin from 'gsap/MorphSVGPlugin';

import { app, webgl } from '@/utils/store';

gsap.registerPlugin(MorphSVGPlugin);

class Menu {
  constructor() {
    this.dom = {
      main: document.querySelector('.a-main'),
      logo: document.getElementById('a-logo'),
      language: document.getElementById('a-language'),
    };

    this.burger = {
      el: document.getElementById('a-burger'),
      path: document.querySelectorAll('#a-burger path'),
    };

    this.menu = {
      el: document.getElementById('a-menu'),
      info: document.querySelectorAll('.menu-info li'),
      container: document.querySelector('#a-menu .menu-container'),
    };

    this.button = {
      el: this.menu.el.querySelectorAll('a'),
      small: this.menu.el.querySelectorAll('.menu-s'),
      large: this.menu.el.querySelectorAll('.menu-l'),
      contact: this.menu.el.querySelector('.menu-contact'),
    };

    this.slider = {
      el: document.getElementById('a-slider'),
      container: document.querySelector('#a-slider .slider-container'),
    };

    this.state = {
      animating: false,
    };
  }

  init() {
    this.set();
    this.events();
  }

  set = () => {
    [...this.button.el].forEach((e) => {
      const span = e.querySelectorAll('span');
      const split = new SplitText(span, { type: 'chars' });

      [...split.chars].forEach((c) => {
        this.wrap(c);
      });
    });
  }

  wrap = (e) => {
    e.style.display = '';
    e.style.position = '';

    const d = document.createElement('div');

    d.classList.add('cube');

    e.parentNode.insertBefore(d, e);

    d.appendChild(e);
  }

  events = () => {
    this.burger.el.addEventListener('click', this.toggle);

    this.dom.logo.addEventListener('click', () => {
      if (app.navigation === false) return;
      this.toggle();
    });

    [...this.button.el].forEach((e) => {
      e.addEventListener('click', this.toggle);
    });

    [...this.button.el].forEach((e) => {
      e.addEventListener('mouseenter', this.enter);
    });

    window.addEventListener('resize', this.resize);
  }

  resize = () => {
    if (app.navigation === true) {
      gsap.set(this.menu.el, {
        clip: `rect(0px ${window.innerWidth}px ${window.innerHeight}px 0px)`,
      });
    } else {
      gsap.set(this.menu.el, {
        clip: `rect(0px ${window.innerWidth}px 0px 0px)`,
      });
    }
  }

  toggle = () => {
    if (this.state.animating === true) return;
    this.state.animating = true;
    if (app.navigation === true) {
      app.navigation = false;
      if (webgl.pause === true) {
        webgl.pause = false;
        webgl.app.play();
      }
      this.hide();
      app.scroll.enable();
    } else {
      app.navigation = true;
      if (webgl.pause === false) {
        webgl.pause = true;
        webgl.app.pause();
      }
      this.show();
      app.scroll.disable();
    }
  }

  show = () => {
    document.querySelector('html').classList.add('is-nav');

    this.menu.el.style.pointerEvents = 'none';

    gsap.set(this.menu.el, {
      display: 'flex',
      clip: `rect(0px ${window.innerWidth}px 0px 0px)`,
    });

    gsap.to(this.menu.el, {
      duration: 1.5,
      ease: 'expo.inOut',
      clip: `rect(0px ${window.innerWidth}px ${window.innerHeight}px 0px)`,
    });

    gsap.set(this.menu.container, {
      y: -window.innerHeight / 3,
    });

    [...this.button.large].forEach((e) => {
      e.classList.add('stagger');
    });

    gsap.to(this.button.large, {
      delay: 0.5,
      stagger: 0.25,
      className: '+=menu-l animate stagger',
    });

    gsap.to(this.menu.container, {
      y: 0,
      duration: 1.5,
      ease: 'expo.inOut',
    });

    gsap.to([this.slider.container, this.dom.main], {
      duration: 1.5,
      ease: 'expo.inOut',
      y: window.innerHeight / 3,
    });

    gsap.set([this.burger.path[0], this.burger.path[1]], {
      opacity: 0,
      delay: 0.5,
    });

    gsap.to(this.dom.logo.querySelectorAll('path'), {
      duration: 1,
      fill: '#e1e1e1',
    });

    gsap.to(this.burger.path[0], {
      duration: 0.5,
      fill: '#e1e1e1',
      morphSVG: 'M24.6 15H24V18H24.6V15Z',
    });

    gsap.to(this.burger.path[1], {
      duration: 0.5,
      fill: '#e1e1e1',
      morphSVG: 'M24.6 0H24V3H24.6V0Z',
    });

    gsap.to(this.burger.path[2], {
      duration: 0.5,
      fill: '#e1e1e1',
      morphSVG: 'M60 8H0V11H60V8Z',
    });

    gsap.set(this.button.small, {
      opacity: 0,
      x: window.innerWidth * 0.05,
    });

    gsap.to(this.button.small, {
      x: 0,
      delay: 0.5,
      opacity: 1,
      duration: 1,
      stagger: 0.25,
      ease: 'expo.out',
    });

    gsap.set(this.menu.info, {
      opacity: 0,
      y: window.innerHeight * 0.05,
    });

    gsap.to(this.menu.info, {
      y: 0,
      delay: 1,
      opacity: 1,
      duration: 1,
      stagger: 0.1,
      ease: 'expo.out',
      onComplete: () => {
        this.state.animating = false;

        [...this.button.large].forEach((e) => {
          e.classList.remove('stagger', 'animate');
        });

        this.menu.el.style.pointerEvents = '';
      },
    });

    if (!Sniffer.isPhone) return;

    gsap.to(this.dom.language, {
      delay: 1,
      autoAlpha: 1,
      duration: 0.5,
    });
  }

  hide = () => {
    document.querySelector('html').classList.remove('is-nav');

    gsap.to(this.menu.el, {
      duration: 1.5,
      ease: 'expo.inOut',
      clip: `rect(0px ${window.innerWidth}px 0px 0px)`,
      onComplete: () => {
        this.state.animating = false;
        this.menu.el.style.display = 'none';
      },
    });

    gsap.to(this.menu.container, {
      duration: 1.5,
      ease: 'expo.inOut',
      y: -window.innerHeight / 3,
    });

    gsap.to([this.slider.container, this.dom.main], {
      y: 0,
      duration: 1.5,
      ease: 'expo.inOut',
    });

    gsap.set([this.burger.path[0], this.burger.path[1]], {
      opacity: 1,
    });

    gsap.to(this.dom.logo.querySelectorAll('path'), {
      duration: 1,
      fill: '#212222',
    });

    gsap.to(this.burger.path[0], {
      duration: 0.5,
      fill: '#212222',
      morphSVG: 'M36 15H24V18H36V15Z',
    });

    gsap.to(this.burger.path[1], {
      duration: 0.5,
      fill: '#212222',
      morphSVG: 'M60 0H24V3H60V0Z',
    });

    gsap.to(this.burger.path[2], {
      duration: 0.5,
      fill: '#212222',
      morphSVG: 'M24 8H0V11H24V8Z',
    });

    if (!Sniffer.isPhone) return;

    gsap.to(this.dom.language, {
      autoAlpha: 0,
      duration: 0.5,
    });
  }

  enter = (e) => {
    if (e.target.classList.contains('menu-contact')) return;

    const button = e.target.querySelector('.menu-l');

    if (button.classList.contains('animate')) return;

    button.classList.add('animate');

    setTimeout(() => {
      button.classList.remove('animate');
    }, 1000);
  }
}

export default Menu;
